import * as React from 'react'
import * as ReactDOM from 'react-dom/client'

import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview'
import Command from '@ckeditor/ckeditor5-core/src/command'
import first from '@ckeditor/ckeditor5-utils/src/first'
import { ImageStyle } from '@ckeditor/ckeditor5-image'
import Plugin from '@ckeditor/ckeditor5-core/src/plugin'
import ToolbarView from '@ckeditor/ckeditor5-ui/src/toolbar/toolbarview'
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils'
import Widget from '@ckeditor/ckeditor5-widget/src/widget'
import WidgetToolbarRepository from '@ckeditor/ckeditor5-widget/src/widgettoolbarrepository'

import { t } from '@owl-nest/localize'

import { UluleImageInsertUI } from '../insert-image/UluleImageInsert'
import { WidgetInstructions, WidgetButton } from './Widget'

import './theme/image-column.css'
import imageColumn from './theme/icons/image-column.svg'
import { View } from '@ckeditor/ckeditor5-ui'

export class ImageColumn extends Plugin {
  static get requires() {
    return [WidgetToolbarRepository, ImageStyle, ImageColumnEditing, ImageColumnUI, UluleImageInsertUI]
  }
}

class ImageColumnUI extends Plugin {
  init() {
    const editor = this.editor

    editor.ui.componentFactory.add('imageColumn', (locale) => {
      const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository)
      //@ts-expect-error _toolbarDefinition is not publicly declared
      const imageToolbarConfig = widgetToolbarRepository._toolbarDefinitions.get('image')
      const imageToolbarView = imageToolbarConfig.view as ToolbarView
      let items: View<HTMLElement>[] = []

      const document = editor.model.document
      document.on('change', () => {
        const selectedElement = document.selection.getSelectedElement()
        const isImageColumn = selectedElement?.is('element', 'imageColumn')
        const isImageColumnChild = document.selection.getFirstPosition()?.findAncestor('imageColumn') !== null
        if (isImageColumn || isImageColumnChild) {
          if (imageToolbarView.items.length > 1) {
            const item3 = imageToolbarView.items.remove(3)
            const item2 = imageToolbarView.items.remove(2)
            const item1 = imageToolbarView.items.remove(1)
            items = [item1, item2, item3]
          }
        } else if (items.length !== 0) {
          imageToolbarView.items.addMany(items)
          items = []
        }
      })

      const command = editor.commands.get('insertImageColumn') as InsertImageColumnCommand

      const buttonView = new ButtonView(locale)

      buttonView.set({
        label: t('Image Column'),
        icon: imageColumn,
        tooltip: true,
      })

      buttonView.bind('isOn', 'isEnabled').to(command, 'value', 'isEnabled')

      this.listenTo(buttonView, 'execute', () => editor.execute('insertImageColumn'))

      return buttonView
    })
  }
}

class ImageColumnEditing extends Plugin {
  static get requires() {
    return [Widget]
  }

  init() {
    this._defineSchema()
    this._defineConverters()

    this.editor.commands.add('insertImageColumn', new InsertImageColumnCommand(this.editor))
  }

  _defineSchema() {
    const schema = this.editor.model.schema

    schema.register('imageColumn', {
      isObject: true,
      isBlock: true,
      allowWhere: '$block',
      allowChildren: ['imageBlock', '$marker', '$text'],
    })

    schema.extend('imageInline', { allowAttributes: 'float' })
    schema.extend('imageBlock', { allowAttributes: 'float' })
  }

  _defineConverters() {
    const conversion = this.editor.conversion

    conversion.for('upcast').elementToElement({
      model: 'imageColumn',
      view: {
        name: 'div',
        classes: 'image-column',
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'imageColumn',
      view: {
        name: 'div',
        classes: 'image-column',
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'imageColumn',
      view: (modelElement, { writer: viewWriter }) => {
        const editor = this.editor
        const ululeImageInsertUIPlugin = this.editor.plugins.get(UluleImageInsertUI)
        const div = viewWriter.createContainerElement('div', { class: 'image-column' })
        const instructions = viewWriter.createUIElement(
          'div',
          { class: 'image-column_instructions' },
          function (domDocument) {
            const domElement = (this as any).toDomElement(domDocument)
            const root = ReactDOM.createRoot(domElement)
            root.render(React.createElement(WidgetInstructions))
            return domElement
          },
        )
        const button = viewWriter.createUIElement('div', { class: 'image-column_add' }, function (domDocument) {
          const domElement = (this as any).toDomElement(domDocument)
          const root = ReactDOM.createRoot(domElement)
          root.render(
            React.createElement(WidgetButton, {
              onClick: () => {
                editor.model.change((writer) => {
                  const viewPosition = viewWriter.createPositionAt(div, 'end')
                  const modelPosition = editor.editing.mapper.toModelPosition(viewPosition)
                  writer.setSelection(modelPosition)
                })
                ululeImageInsertUIPlugin.openModal()
              },
            }),
          )

          return domElement
        })

        viewWriter.insert(viewWriter.createPositionAt(div, 0), button)
        viewWriter.insert(viewWriter.createPositionAt(div, 0), instructions)

        return toWidget(div, viewWriter)
      },
    })

    this.editor.conversion.for('upcast').add((dispatcher) => {
      dispatcher.on(
        'element:img',
        (event, data, conversionApi) => {
          if (!data.modelRange) {
            return
          }

          const viewElement = data.viewItem
          const modelImageElement = first(data.modelRange.getItems())

          // Run this converter only if an image has been found in the model.
          // In some cases it may not be found (for example if we run this on a figure with different type than image).
          if (!modelImageElement) {
            return
          }

          // ...and the `imageStyle` attribute is allowed for that element, otherwise stop conversion early.
          if (!conversionApi.schema.checkAttribute(modelImageElement, 'float')) {
            return
          }

          const float = viewElement.getStyle('float')

          if (float) {
            conversionApi.writer.setAttribute('float', float, modelImageElement)
          }
        },
        { priority: 'low' },
      )
    })

    this.editor.conversion.for('downcast').add((dispatcher) => {
      dispatcher.on('attribute:float', (event, data, conversionApi) => {
        if (!conversionApi.consumable.consume(data.item, event.name)) {
          return
        }

        const viewElement = conversionApi.mapper.toViewElement(data.item)

        conversionApi.writer.setStyle('float', data.attributeNewValue, viewElement)
      })
    })
  }
}

class InsertImageColumnCommand extends Command {
  declare value: boolean
  declare isEnabled: boolean

  execute() {
    this.editor.model.change((writer) => {
      this.editor.model.insertContent(writer.createElement('imageColumn'))
    })
  }

  refresh() {
    const model = this.editor.model
    const selection = model.document.selection
    const position = selection.getFirstPosition()
    const allowedIn = position ? model.schema.findAllowedParent(position, 'imageColumn') : null

    this.isEnabled = allowedIn !== null
  }
}
